import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Archive, ArrowCounterClockwise, ArrowSquareOut, Bank, Buildings, Cake, CalendarCheck, ChatCircle, Check, ClipboardText, DeviceMobile, EnvelopeSimple, Eye, EyeSlash,
  FolderSimple, Gauge, Handshake, IdentificationCard, Key, LockSimple, LockSimpleOpen, Phone, Receipt, Sparkle, Trash, User, Wallet, Wrench, XCircle,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, KpiReihe, KpiTile, SchalterZeile, SegmentedControl, TextFeld, ToastPill, useToastPill,
} from '../../../../../components/immobilien/ui';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { passwortFehler, passwortVorschlag } from '../../../../../lib/immobilien/portal';
import { ZustellwegeAuswahl } from '../../../../../components/schreiben/ZustellwegeAuswahl';
import { kanaeleGleich, ladeKanaele, speichereKanaele, type KontaktKanal } from '../../../../../lib/kontakt/kontaktKanaele';
import { loescheMieter } from '../../../../../lib/mieter/loeschen';
import { mietdauerKurz, personenText } from '../../../../../lib/mieter/detail';
import { ANREDEN, type Anrede } from '../../../../../lib/mieter/neuerMieter';
import { gebePortalFrei, setzePortalStatus } from '../../../../../lib/mieter/portalZugang';
import {
  archivFehler, archivPatch, archivVorschlag, istEhemalig, reaktivierenPatch, kanaeleAusSpalten, kanaeleMitPrimaer, kuendigungsfristText, kuendigungZum, stammdatenAusMieter, stammdatenFehler, stammdatenGeaendert, stammdatenPayload,
  type StammdatenEntwurf,
} from '../../../../../lib/mieter/stammdaten';
import { istGekuendigt, PORTAL_ANZEIGE } from '../../../../../lib/mieter/liste';
import { meldeMieterGeaendert } from '../../../../../lib/mieter/useMieterDetail';
import { supabase } from '../../../../../lib/supabase/client';
import { DatenZeile } from '../../../objekte/einheit/bereiche/teile';
import { TerminFormularKarte } from '../../../objekte/gemeinsam/TerminFormularKarte';
import { NeuesPasswortSenden } from '../../../../../components/portal/NeuesPasswortSenden';
import { TestphaseKarte } from '../../../../../components/portal/TestphaseKarte';
import { testphaseFuerDatensatz } from '../../../../../lib/portal/schalter';
import { sendeZugangsdaten, zugangsMeldung } from '../../../../../lib/portal/zugangsdaten';
import { useMieterSeite } from '../MieterSeitenKontext';
import { WeitereKontaktwege } from '../../../../../components/kontakt/WeitereKontaktwege';

const GLAS: React.CSSProperties = { background: 'var(--glass)' };
const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');

// ── Archivieren als ehemaliger Mieter (Freigabe 13.10.2026) ────────────────────
/** Knopf + Formular „Archivieren“ bzw. „Wieder aktivieren“ – in Stammdaten und Mietverhältnis. */
function useArchiv(zeige: (text: string, fehler?: boolean) => void, groesse: 36 | 44): { knopf: React.ReactNode; karte: React.ReactNode } {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const ehemalig = istEhemalig(m.status);
  const [offen, setOffen] = useState(false);
  const [auszug, setAuszug] = useState(() => archivVorschlag(m.mietende, s.heute));
  const [portalSperren, setPortalSperren] = useState(s.portal === 'aktiv');
  const [reaktivieren, setReaktivieren] = useState(false);
  const [laeuft, setLaeuft] = useState(false);

  const archiviere = async () => {
    const f = archivFehler(auszug, m.mietbeginn, s.heute);
    if (f) { zeige(f, true); return; }
    setLaeuft(true);
    try {
      const { error } = await supabase.from('mieter').update(archivPatch(auszug)).eq('id', m.id);
      if (error) throw new Error(error.message);
      if (portalSperren && s.portal === 'aktiv' && s.email) await setzePortalStatus(s.email, 'Inaktiv');
      setOffen(false);
      zeige(`${s.name} als ehemaliger Mieter archiviert`);
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht archiviert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };
  const aktiviere = async () => {
    setLaeuft(true);
    const { error } = await supabase.from('mieter').update(reaktivierenPatch(m.mietende, s.heute)).eq('id', m.id);
    setLaeuft(false);
    setReaktivieren(false);
    if (error) { zeige(`Nicht geändert: ${error.message}`, true); return; }
    zeige(`${s.name} ist wieder aktiv`);
    meldeMieterGeaendert(m.id);
  };

  const knopf = ehemalig
    ? <ImmoButton groesse={groesse} icon={ArrowCounterClockwise} disabled={laeuft} onClick={() => setReaktivieren(true)}>Wieder aktivieren</ImmoButton>
    : <ImmoButton groesse={groesse} icon={Archive} disabled={laeuft} aria-expanded={offen} onClick={() => setOffen(o => !o)}>Archivieren</ImmoButton>;
  const karte = (
    <>
      {offen && !ehemalig && (
        <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
          <KartenTitel icon={Archive}>Als ehemaligen Mieter archivieren</KartenTitel>
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>
            Der Mieter wohnt danach nicht mehr in der Einheit und erscheint in der Liste unter „Inaktiv“. Verlauf, Dokumente, Tickets und Verträge bleiben erhalten; Wieder aktivieren ist jederzeit möglich.
          </span>
          <FeldRaster minBreite={200}>
            <TextFeld label="Ausgezogen am (Mietende)" type="date" icon={CalendarCheck} wert={auszug} onChange={setAuszug} max={s.heute} />
          </FeldRaster>
          {s.portal === 'aktiv' && (
            <SchalterZeile label="Portal-Zugang sperren" icon={LockSimple} an={portalSperren} onChange={setPortalSperren} />
          )}
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => setOffen(false)}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" icon={Archive} disabled={laeuft} onClick={() => { void archiviere(); }}>{laeuft ? 'Wird archiviert …' : 'Archivieren'}</ImmoButton>
          </div>
        </InnenKarte>
      )}
      {reaktivieren && (
        <BestaetigenDialog titel="Wieder aktivieren?" text="Der Mieter gilt danach wieder als wohnend; ein bereits erreichtes Mietende wird entfernt."
          bestaetigenLabel="Aktivieren" icon={ArrowCounterClockwise} gefahr={false} laeuft={laeuft}
          onBestaetigen={() => { void aktiviere(); }} onAbbrechen={() => setReaktivieren(false)} />
      )}
    </>
  );
  return { knopf, karte };
}

// ── Stammdaten & Kontakt (Screens 26, 27) ──────────────────────────────────────
export function StammdatenPanel() {
  const s = useMieterSeite();
  const navigate = useNavigate();
  const m = s.daten.mieter;
  const [start, setStart] = useState<StammdatenEntwurf>(() => stammdatenAusMieter(m));
  const [e, setE] = useState<StammdatenEntwurf>(start);
  const [kanaele, setKanaele] = useState<KontaktKanal[] | null>(null);
  const [kanalFehler, setKanalFehler] = useState<string | null>(null);
  const [kanaeleStart, setKanaeleStart] = useState<KontaktKanal[] | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [verwerfen, setVerwerfen] = useState(false);
  const [loeschen, setLoeschen] = useState(false);
  const [loescht, setLoescht] = useState(false);
  const [toast, zeige] = useToastPill();
  const setze = <K extends keyof StammdatenEntwurf>(k: K) => (v: StammdatenEntwurf[K]) => setE(alt => ({ ...alt, [k]: v }));
  const fehler = stammdatenFehler(e);
  // #520: auch weitere E-Mails/Telefonnummern zählen als Änderung
  const geaendert = stammdatenGeaendert(e, start) || !kanaeleGleich(kanaele, kanaeleStart);
  const archiv = useArchiv(zeige, 44);

  useEffect(() => {
    let aktiv = true;
    ladeKanaele(supabase, 'mieter', m.id)
      .then(k => { if (aktiv) { const g = k.length ? k : kanaeleAusSpalten(m); setKanaele(g); setKanaeleStart(g); } })
      .catch((err: unknown) => { if (aktiv) setKanalFehler(fehlerText(err)); });
    return () => { aktiv = false; };
  }, [m]);

  // Rückfrage bei ungespeicherten Änderungen (Escape, „Alle Widgets“)
  const { registriereVerlassen, schliessePanel } = s;
  useEffect(() => {
    registriereVerlassen(geaendert ? () => setVerwerfen(true) : null);
    return () => registriereVerlassen(null);
  }, [geaendert, registriereVerlassen]);

  const speichere = async () => {
    if (Object.keys(fehler).length) { zeige('Bitte die markierten Felder prüfen', true); return; }
    if (!kanaele) { zeige(`E-Mail/Telefon nicht geladen${kanalFehler ? `: ${kanalFehler}` : ''}`, true); return; }
    setLaeuft(true);
    try {
      const { error } = await supabase.from('mieter').update(stammdatenPayload(e, start)).eq('id', m.id);
      if (error) throw new Error(error.message);
      const neu = kanaeleMitPrimaer(kanaele, e.email, e.telefon);
      await speichereKanaele(supabase, 'mieter', m.id, neu);
      setKanaele(neu);
      setKanaeleStart(neu);
      setStart(e);
      zeige('Stammdaten gespeichert');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht gespeichert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
    }
  };

  const loesche = async () => {
    setLoescht(true);
    try {
      await loescheMieter(m.id);
      registriereVerlassen(null);
      navigate('/admin/mieter', { replace: true });
    } catch (err) {
      setLoescht(false);
      setLoeschen(false);
      zeige(`Nicht gelöscht: ${fehlerText(err)}`, true);
    }
  };

  const telefon = e.telefon.trim();
  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Stammdaten</KartenTitel>
        <SegmentedControl<Anrede | ''> ariaLabel="Anrede" hoehe={34} className="self-start" wert={e.anrede} onChange={setze('anrede')}
          segmente={ANREDEN.map(a => ({ key: a, label: a, icon: User }))} />
        {e.anrede === '' && m.anrede && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Anrede bisher: „{String(m.anrede)}“</span>}
        <FeldRaster minBreite={180}>
          {(start.firma || e.firma) && <TextFeld label="Firma" icon={Buildings} wert={e.firma} onChange={setze('firma')} voll />}
          <TextFeld label="Vorname" icon={User} wert={e.vorname} onChange={setze('vorname')} fehler={fehler.name && !e.vorname ? fehler.name : undefined} />
          <TextFeld label="Nachname" icon={User} wert={e.nachname} onChange={setze('nachname')} fehler={fehler.name && !e.nachname ? fehler.name : undefined} />
          <TextFeld label="Geburtsdatum" icon={Cake} type="date" wert={e.geburtsdatum} onChange={setze('geburtsdatum')} fehler={fehler.geburtsdatum} />
          <TextFeld label="E-Mail" icon={EnvelopeSimple} type="email" wert={e.email} onChange={setze('email')} fehler={fehler.email} autoComplete="off" />
          <TextFeld label="Telefon" icon={Phone} type="tel" wert={e.telefon} onChange={setze('telefon')} autoComplete="off" />
          <TextFeld label="IBAN" icon={Bank} wert={e.iban} onChange={setze('iban')} fehler={fehler.iban} autoComplete="off" />
          <TextFeld label="Kontoinhaber" icon={IdentificationCard} wert={e.kontoinhaber} onChange={setze('kontoinhaber')} autoComplete="off" />
        </FeldRaster>
        {kanaele && <WeitereKontaktwege kanaele={kanaele} onChange={setKanaele} disabled={laeuft} />}
        <ZustellwegeAuswahl art="mieter" id={m.id} />
        <div className="flex justify-end">
          <ImmoButton art="akzent" groesse={44} icon={Check} disabled={laeuft || !geaendert} onClick={() => { void speichere(); }}>
            {laeuft ? 'Wird gespeichert …' : 'Speichern'}
          </ImmoButton>
        </div>
      </InnenKarte>
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        {telefon
          ? <a href={`tel:${telefon.replace(/[^\d+]/g, '')}`} className="immo-hover-flaeche inline-flex items-center" style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}><Phone weight="bold" size={14} />Anrufen</a>
          : <ImmoButton groesse={44} icon={Phone} disabled title="Keine Telefonnummer hinterlegt">Anrufen</ImmoButton>}
        {e.email
          ? <a href={`mailto:${e.email}`} className="immo-hover-flaeche inline-flex items-center" style={{ height: 44, padding: '0 16px', gap: 7, borderRadius: 22, background: 'var(--fill2)', color: 'var(--ink)', fontSize: 13, fontWeight: 700, textDecoration: 'none' }}><EnvelopeSimple weight="bold" size={14} />E-Mail</a>
          : <ImmoButton groesse={44} icon={EnvelopeSimple} disabled title="Keine E-Mail hinterlegt">E-Mail</ImmoButton>}
        {archiv.knopf}
        <ImmoButton groesse={44} icon={Trash} onClick={() => setLoeschen(true)}>Mieter löschen</ImmoButton>
      </div>
      {archiv.karte}

      {verwerfen && (
        <BestaetigenDialog titel="Änderungen verwerfen?" text="Die geänderten Stammdaten gehen verloren." bestaetigenLabel="Verwerfen" icon={ArrowCounterClockwise}
          onBestaetigen={() => { setVerwerfen(false); registriereVerlassen(null); schliessePanel(); }} onAbbrechen={() => setVerwerfen(false)} />
      )}
      {loeschen && (
        <BestaetigenDialog titel="Mieter löschen?" text="Das kann nicht rückgängig gemacht werden. Kontaktwege, Notizen, protokollierte Aktivitäten, Verknüpfungen und die Dokumente des Mieters werden mit gelöscht; Tickets, Termine, Verträge und Zähler bleiben ohne Mieterbezug erhalten. Ausgezogene Mieter besser archivieren."
          detail={<><b style={{ fontWeight: 700 }}>{s.name}</b><br /><span style={{ fontSize: 12, color: 'var(--ink3)' }}>{[s.objektName, s.einheit].filter(Boolean).join(' · ')}</span></>}
          bestaetigenLabel="Löschen" icon={Trash} laeuft={loescht} onBestaetigen={() => { void loesche(); }} onAbbrechen={() => setLoeschen(false)} />
      )}
    </>
  );
}

// ── Mietverhältnis (Screen 24) ─────────────────────────────────────────────────
export function MietverhaeltnisPanel() {
  const s = useMieterSeite();
  const navigate = useNavigate();
  const m = s.daten.mieter;
  const [toast, zeige] = useToastPill();
  const vertrag = s.daten.vertraege.find(v => v.kuendigungsfrist_monate) ?? s.daten.vertraege[0] ?? null;
  const frist = vertrag?.kuendigungsfrist_monate ?? null;
  const gekuendigt = istGekuendigt(m.status);
  const kuendigungEingang = typeof m.kuendigung_eingang === 'string' && m.kuendigung_eingang ? m.kuendigung_eingang.slice(0, 10) : null;
  const [kuendigung, setKuendigung] = useState(false);
  const [eingang, setEingang] = useState(s.heute);
  const [mietende, setMietende] = useState(() => kuendigungZum(s.heute, frist ?? 3));
  const [zuruecknehmen, setZuruecknehmen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [uebergabe, setUebergabe] = useState(false);
  const archiv = useArchiv(zeige, 36);
  const zahlungsweise = typeof m.zahlungsweise === 'string' && m.zahlungsweise.trim() ? m.zahlungsweise.trim() : '–';
  const mvPfad = m.objekt_id && m.einheit_id
    ? `/admin/objekte/${encodeURIComponent(m.objekt_id)}/einheiten/${encodeURIComponent(m.einheit_id)}?bereich=mvedit&mieter=${encodeURIComponent(m.id)}&reiter=vertrag`
    : null;

  const aendereStatus = async (patch: Record<string, unknown>, meldung: string) => {
    setLaeuft(true);
    const { error } = await supabase.from('mieter').update(patch).eq('id', m.id);
    setLaeuft(false);
    if (error) { zeige(`Nicht gespeichert: ${error.message}`, true); return false; }
    zeige(meldung);
    meldeMieterGeaendert(m.id);
    return true;
  };

  const erfasseKuendigung = async () => {
    if (!eingang) { zeige('Bitte angeben, wann die Kündigung eingegangen ist.', true); return; }
    if (eingang > s.heute) { zeige('Das Eingangsdatum liegt in der Zukunft.', true); return; }
    if (!mietende) { zeige('Bitte das Mietende angeben.', true); return; }
    if (mietende < eingang) { zeige('Das Mietende liegt vor dem Eingang der Kündigung.', true); return; }
    if (s.beginn && mietende < s.beginn) { zeige('Das Mietende liegt vor dem Mietbeginn.', true); return; }
    if (await aendereStatus({ status: 'gekündigt', mietende, kuendigung_eingang: eingang }, `Kündigung erfasst – Mietende ${deDatum(mietende)}`)) setKuendigung(false);
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <InnenKarte style={GLAS}>
        <KartenTitel>Mietverhältnis</KartenTitel>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(360px, 100%), 1fr))', columnGap: 28 }}>
          <DatenZeile label="Objekt" wert={s.objektName} />
          <DatenZeile label="Einheit" wert={s.einheit} />
          <DatenZeile label="Mietbeginn" wert={deDatum(s.beginn)} zahl />
          <DatenZeile label="Mietende" wert={s.ende ? deDatum(s.ende) : 'unbefristet'} zahl />
          <DatenZeile label="Mietdauer" wert={s.status === 'einzug' ? 'Einzug geplant' : mietdauerKurz(s.beginn, s.heute)} />
          <DatenZeile label="Personen" wert={String(s.personen)} zahl />
          <DatenZeile label="Zahlungsweise" wert={zahlungsweise} />
          <DatenZeile label="Kündigungsfrist" wert={kuendigungsfristText(frist)} />
          {kuendigungEingang && <DatenZeile label="Kündigung eingegangen" wert={deDatum(kuendigungEingang)} zahl />}
        </div>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          {gekuendigt
            ? <ImmoButton icon={ArrowCounterClockwise} disabled={laeuft} onClick={() => setZuruecknehmen(true)}>Kündigung zurücknehmen</ImmoButton>
            : s.status !== 'inaktiv' && <ImmoButton icon={XCircle} disabled={laeuft} onClick={() => setKuendigung(k => !k)} aria-expanded={kuendigung}>Kündigung erfassen</ImmoButton>}
          <ImmoButton icon={ClipboardText} onClick={() => setUebergabe(u => !u)} aria-expanded={uebergabe}>Übergabe planen</ImmoButton>
          {m.objekt_id && <ImmoButton icon={Buildings} onClick={() => navigate(`/admin/objekte/${encodeURIComponent(m.objekt_id ?? '')}`)}>Zum Objekt</ImmoButton>}
          {mvPfad && <ImmoButton icon={Handshake} onClick={() => navigate(mvPfad)}>Mietverhältnis bearbeiten</ImmoButton>}
          {archiv.knopf}
        </div>
      </InnenKarte>
      {archiv.karte}

      {kuendigung && !gekuendigt && (
        <InnenKarte style={{ boxShadow: 'inset 0 0 0 2px var(--acc)' }}>
          <KartenTitel icon={XCircle}>Kündigung erfassen</KartenTitel>
          <FeldRaster minBreite={200}>
            <TextFeld label="Kündigung eingegangen am" type="date" icon={CalendarCheck} wert={eingang} max={s.heute}
              onChange={v => { setEingang(v); if (v) setMietende(kuendigungZum(v, frist ?? 3)); }} />
            <TextFeld label="Mietende" type="date" icon={CalendarCheck} wert={mietende} onChange={setMietende} />
          </FeldRaster>
          <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
            Mietende als Vorschlag gemäß Kündigungsfrist: {kuendigungsfristText(frist)}. Beide Daten werden gespeichert.
          </span>
          <div className="flex justify-end flex-wrap" style={{ gap: 8 }}>
            <ImmoButton onClick={() => setKuendigung(false)}>Abbrechen</ImmoButton>
            <ImmoButton art="akzent" icon={Check} disabled={laeuft} onClick={() => { void erfasseKuendigung(); }}>{laeuft ? 'Wird gespeichert …' : 'Kündigung speichern'}</ImmoButton>
          </div>
        </InnenKarte>
      )}

      {uebergabe && (
        <>
          <TerminFormularKarte termin={null} prefillDate={s.ende} prefillObjektId={m.objekt_id} prefillTyp="sonstig"
            prefillTitel={`Wohnungsübergabe ${s.name}${s.einheit ? ` · ${s.einheit}` : ''}`} prefillMieterId={m.id}
            onAbbrechen={() => setUebergabe(false)} onGespeichert={() => { setUebergabe(false); zeige('Übergabe-Termin angelegt'); meldeMieterGeaendert(m.id); }} />
          <InnenKarte style={GLAS}>
            <KartenTitel icon={ClipboardText}>Übergabeprotokoll</KartenTitel>
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Das Protokoll (Räume, Zähler, Schlüssel, Unterschriften) wird am Übergabetag im Übergabe-Assistenten erfasst.</span>
            <ImmoButton icon={ArrowSquareOut} style={{ alignSelf: 'flex-start' }} onClick={() => navigate('/admin/uebergabeprotokolle')}>Zu den Übergabeprotokollen</ImmoButton>
          </InnenKarte>
        </>
      )}

      {zuruecknehmen && (
        <BestaetigenDialog titel="Kündigung zurücknehmen?" text="Das Mietverhältnis läuft danach unbefristet weiter; das eingetragene Mietende wird entfernt."
          bestaetigenLabel="Zurücknehmen" icon={ArrowCounterClockwise} gefahr={false} laeuft={laeuft}
          onBestaetigen={() => { void aendereStatus({ status: 'aktiv', mietende: null, kuendigung_eingang: null }, 'Kündigung zurückgenommen').then(() => setZuruecknehmen(false)); }}
          onAbbrechen={() => setZuruecknehmen(false)} />
      )}
    </>
  );
}

// ── Mieterportal (Screens 28, 29, 46) ──────────────────────────────────────────
const BERECHTIGUNGEN = [
  { label: 'Dokumente einsehen', icon: FolderSimple },
  { label: 'Mietkonto & Zahlungen', icon: Wallet },
  { label: 'Schäden & Tickets melden', icon: Wrench },
  { label: 'Zählerstände melden', icon: Gauge },
  { label: 'Nachrichten', icon: ChatCircle },
  { label: 'NK-Abrechnungen', icon: Receipt },
] as const;

export function PortalPanel() {
  const s = useMieterSeite();
  const m = s.daten.mieter;
  const [toast, zeige] = useToastPill();
  const [mailSenden, setMailSenden] = useState(true);
  const [passwort, setPasswort] = useState('');
  const [sichtbar, setSichtbar] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [sperren, setSperren] = useState(false);
  const p = PORTAL_ANZEIGE[s.portal];
  const status = !s.email ? 'ohneEmail' : s.portal;
  const pFehler = useMemo(() => (passwort ? passwortFehler(passwort) : null), [passwort]);

  const freigeben = async () => {
    const f = passwortFehler(passwort);
    if (f) { zeige(f, true); return; }
    setLaeuft(true);
    try {
      await gebePortalFrei({ name: s.name, email: s.email, passwort, mieterId: m.id });
      // #606: Portal gleich für diese Person öffnen (Testphase) – sonst sieht sie nur „bald verfügbar“
      const tf = await testphaseFuerDatensatz('mieterportal', 'mieter', m.id);
      const tfHinweis = tf ? ` – Portal aber noch nicht freigeschaltet: ${tf}` : '';
      if (mailSenden) {
        try { const r = await sendeZugangsdaten({ rolle: 'mieter', id: m.id, passwort, neuSetzen: false }); zeige(`Zugang aktiviert – ${zugangsMeldung(r, true)}${tfHinweis}`, !r.gesendet || !!tf); }
        catch (e) { zeige(`Zugang aktiviert, E-Mail aber nicht gesendet: ${fehlerText(e)} – Passwort bitte selbst mitteilen${tfHinweis}`, true); }
      } else zeige(`Portal-Zugang für ${s.email} aktiviert – Passwort dem Mieter mitteilen${tfHinweis}`, !!tf);
      setPasswort('');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(fehlerText(err), true);
    } finally {
      setLaeuft(false);
    }
  };
  const setzeStatus = async (neu: 'Aktiv' | 'Inaktiv') => {
    setLaeuft(true);
    try {
      await setzePortalStatus(s.email, neu);
      zeige(neu === 'Inaktiv' ? 'Portal-Zugang gesperrt' : 'Portal-Zugang wieder freigegeben');
      meldeMieterGeaendert(m.id);
    } catch (err) {
      zeige(`Nicht geändert: ${fehlerText(err)}`, true);
    } finally {
      setLaeuft(false);
      setSperren(false);
    }
  };

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <KpiReihe minBreite={240}>
        <KpiTile layout="kachel" label="Status" icon={DeviceMobile} ton={p.ton} wertFarbig wert={status === 'ohneEmail' ? 'E-Mail fehlt' : p.label} />
        <KpiTile layout="kachel" label="Login / Einladung an" icon={EnvelopeSimple} ton="info" wert={s.email || '–'} />
      </KpiReihe>

      <InnenKarte style={GLAS}>
        <KartenTitel>{status === 'aktiv' ? 'Zugang' : status === 'gesperrt' ? 'Zugang gesperrt' : 'Zugang freigeben'}</KartenTitel>
        <TextFeld label="E-Mail für die Einladung" icon={EnvelopeSimple} wert={s.email} onChange={() => undefined} readOnly
          placeholder="Keine E-Mail hinterlegt" />
        {status === 'ohneEmail' && (
          <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Für den Zugang braucht der Mieter eine E-Mail-Adresse.</span>
            <ImmoButton icon={User} onClick={() => s.oeffnePanel('kontakt')}>In Stammdaten ergänzen</ImmoButton>
          </div>
        )}
        {status !== 'ohneEmail' && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Die E-Mail-Adresse wird in den Stammdaten geändert.</span>}

        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))', gap: 8 }}>
          {BERECHTIGUNGEN.map(b => <SchalterZeile key={b.label} label={b.label} icon={b.icon} an={status === 'aktiv'} onChange={() => undefined} disabled />)}
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink3)' }}>Einzelne Bereiche lassen sich noch nicht getrennt schalten – ein freigeschalteter Zugang sieht alle Bereiche, die es im Portal schon gibt.</span>
        {status === 'aktiv' && <TestphaseKarte art="mieterportal" tabelle="mieter" datensatzId={m.id} name={s.name} zeige={zeige} />}

        {status === 'kein' && (
          <form className="flex flex-col" style={{ gap: 10, padding: 12, borderRadius: 18, background: 'var(--fill)' }}
            onSubmit={ev => { ev.preventDefault(); if (!laeuft && passwort && !pFehler) void freigeben(); }}>
            <input type="email" autoComplete="username" value={s.email} readOnly hidden />
            <div className="flex items-end flex-wrap" style={{ gap: 8 }}>
              <div style={{ flex: '1 1 220px' }}>
                <TextFeld label="Portal-Passwort" type={sichtbar ? 'text' : 'password'} wert={passwort} onChange={setPasswort} autoComplete="new-password"
                  fehler={pFehler ?? undefined} placeholder="mindestens 6 Zeichen" />
              </div>
              <ImmoButton groesse={48} pille={false} icon={sichtbar ? EyeSlash : Eye} aria-label={sichtbar ? 'Passwort verbergen' : 'Passwort anzeigen'} onClick={() => setSichtbar(v => !v)} />
              <ImmoButton groesse={48} pille={false} icon={Sparkle} onClick={() => { setPasswort(passwortVorschlag()); setSichtbar(true); }}>Vorschlagen</ImmoButton>
            </div>
            <SchalterZeile label={`Zugangsdaten per E-Mail an ${s.email} senden (Passwort im Klartext)`} icon={EnvelopeSimple} an={mailSenden} onChange={setMailSenden} />
            <span style={{ fontSize: 12, color: 'var(--ink3)' }}>Der Mieter meldet sich mit seiner E-Mail und diesem Passwort an. Ohne E-Mail bitte das Passwort selbst mitteilen. Das Portal wird damit gleich für diese Person freigeschaltet (Testphase) – für alle anderen bleibt es aus.</span>
          </form>
        )}

        <div className="flex flex-wrap justify-end" style={{ gap: 8 }}>
          {(status === 'aktiv' || status === 'gesperrt') && (
            <NeuesPasswortSenden rolle="mieter" id={m.id} email={s.email} zeige={zeige} />
          )}
          {status === 'aktiv' && <ImmoButton art="bad" icon={LockSimple} disabled={laeuft} onClick={() => setSperren(true)}>Zugang sperren</ImmoButton>}
          {status === 'gesperrt' && <ImmoButton art="akzent" icon={LockSimpleOpen} disabled={laeuft} onClick={() => { void setzeStatus('Aktiv'); }}>Zugang wieder freigeben</ImmoButton>}
          {status === 'kein' && (
            <ImmoButton art="akzent" groesse={44} icon={Key} disabled={laeuft || !passwort || !!pFehler} onClick={() => { void freigeben(); }}>
              {laeuft ? 'Wird angelegt …' : 'Zugang freigeben'}
            </ImmoButton>
          )}
        </div>
      </InnenKarte>

      {sperren && (
        <BestaetigenDialog titel="Zugang sperren?" text={`${s.name} kann sich danach nicht mehr im Portal anmelden. Freigeben ist jederzeit möglich.`}
          bestaetigenLabel="Sperren" icon={LockSimple} laeuft={laeuft} onBestaetigen={() => { void setzeStatus('Inaktiv'); }} onAbbrechen={() => setSperren(false)} />
      )}
    </>
  );
}
